// src/sections/Credits.jsx — member / tenant-admin credits view (route /credits).
// SA in adminMode is bounced to /admin (this view is tenant-scoped). A hybrid SA
// NOT in adminMode views their active tenant here and sees money.
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchCreditBalance, fetchCreditUsage, fetchWalletSplit } from '../services/api/creditsApi.js';
import CreditBalanceCard from '../components/credits/CreditBalanceCard.jsx';
import CreditUsageTable from '../components/credits/CreditUsageTable.jsx';
import { parseDecimal } from '../utils/creditsFormat.js';
import { readTopUpBaseline, clearTopUpBaseline } from '../billing/topUpBaseline.js';
import { refreshEntitlements } from '../hooks/useEntitlements.js';
import { Wallet } from 'lucide-react';
import SectionCard, { PAGE_COLUMN, CARD_BODY } from '../components/ui/SectionCard.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

const FONT = FONT_SANS;
const COLOR = { text: COLORS.ink, muted: COLORS.textSecondary, errFg: SEMANTIC.errore.fg };

// ~30 s of waiting for the webhook. Same shape as CheckoutReturn's budget: long
// enough for the normal case, short enough not to spin for ever on a lost event.
export const TOPUP_POLL_MS = 3000;
export const TOPUP_MAX_POLLS = 10;

// ⭐ ONLY a confirmed credit earns green. 'waiting' is still polling, 'slow'
// spent its budget without seeing the pack, and 'settled' means we could not
// measure the outcome at all — its own copy tells the reader to check the
// balance and not to buy again. Green over a payment that may not have been
// credited is the failure mode the colour rule exists to prevent.
const TOPUP_DONE = new Set(['credited']);

export default function Credits() {
  const { t } = useTranslation('credits');
  const { t: tAuth } = useTranslation('auth');
  const {
    activeTenant, tenants, isSuperadmin, adminMode, isCustomerAdminOf, notePermissionsGranted,
  } = useAuth();
  const tenantId = activeTenant?.tenant_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  // PB5 wallet split: null = panel hidden (not for this user, not probed, or
  // failed); an object = render it.
  const [split, setSplit] = useState(null);

  // ── Return from a top-up checkout (?topup=done) ─────────────────────────
  // ⛔ The redirect proves a PAYMENT, never an ENTITLEMENT: the pack is credited
  // by the webhook. Reading the balance once on mount and saying nothing is how
  // someone who has just paid concludes it failed — and buys a second time.
  // Same reasoning as CheckoutReturn, on the subscription side.
  // ⭐ It polls through THIS page's own reader and feeds `balance`, so the card
  // above updates with it rather than through a second subscription.
  // ⚠️ The page is not the whole screen, though: the sidebar rail holds its own
  // useEntitlements instance and outlives this route, so a confirmed credit also
  // has to nudge that store — see refreshEntitlements() below.
  //
  // ⚠️ The baseline is taken BEFORE leaving for Stripe (the pack checkout stashes it),
  // never from the first balance read on return. Measured 2026-09-07: the
  // webhook landed at 12:59:46, BEFORE the page rendered, so that first read
  // already showed the credited total and "has it gone up since?" was false
  // for ever — the banner sat on "payment received" and then claimed crediting
  // was slow, on a purchase that had already succeeded. The webhook beating the
  // browser is the NORMAL case, not the edge one.
  // 'waiting' | 'credited' | 'settled' | 'slow' | 'noWorkspace' —
  // null on an ordinary visit.
  const { search } = useLocation();
  const navigate = useNavigate();
  const [topUp, setTopUp] = useState(
    () => (new URLSearchParams(search).get('topup') === 'done' ? 'waiting' : null),
  );
  const topUpBaseline = useRef(undefined);

  useEffect(() => {
    if (isSuperadmin && adminMode) return undefined; // bounced below
    if (!tenantId) { setLoading(false); return undefined; } // no active tenant → don't spin forever

    let cancelled = false;
    setLoading(true);
    setError('');
    setSplit(null);

    Promise.allSettled([
      fetchCreditBalance({ tenant: tenantId }),
      fetchCreditUsage({ tenant: tenantId }),
    ]).then((results) => {
      if (cancelled) return;
      const [b, u] = results;
      setBalance(b.status === 'fulfilled' ? b.value : null);
      setUsage(u.status === 'fulfilled' ? u.value : null);
      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length === results.length) {
        setError(rejected[0].reason?.message || t('errors.loadFailed'));
      } else if (rejected.length > 0) {
        setError(t('errors.partial'));
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
    // `t` intentionally omitted: a language switch must not refetch credits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, adminMode, tenantId]);

  // PB5 — the customer's wallet split per workspace. The guide's recommended
  // strategy: only once /balance has answered with a non-null customer_id, and
  // only for a customer-admin of THAT customer (the pre-gate saves a probe that
  // would 403). ⛔ The 403 CUSTOMER_ADMIN_REQUIRED path is still handled below
  // regardless, because `customers[]` is only as fresh as the login token: a
  // revoked admin still passes the pre-gate until re-auth, and the server is the
  // real gate. That 403 means "not for this user" — the panel stays hidden,
  // silently: no error toast, no retry. Any other failure also just hides the
  // panel; the split is an extra, never the reason this page errors.
  // A tenant with no customer never reaches here (customer_id null); if it did,
  // the endpoint answers 200 with a single bucket and the table renders it as is.
  const customerId = balance?.customer_id || null;
  useEffect(() => {
    if (!tenantId || !customerId || !isCustomerAdminOf?.(customerId)) { setSplit(null); return undefined; }
    let cancelled = false;
    fetchWalletSplit({ tenant: tenantId })
      .then((data) => { if (!cancelled) setSplit(data); })
      .catch(() => { if (!cancelled) setSplit(null); });
    return () => { cancelled = true; };
  }, [tenantId, customerId, isCustomerAdminOf]);

  // Poll for the pack to land. Bounded, and honest when the budget runs out:
  // "still being credited" is the truth, and never a claim that it failed.
  // ⚠️ The baseline is `remaining_credits`, NOT `expiring_credits`: a top-up
  // never expires, so it raises the first and leaves the second alone.
  //
  // ⛔ `balance` must NOT be a dependency here, even though the body writes it.
  // Each poll calls setBalance with a FRESH object from the network, so listing
  // it would re-run this effect on every tick: the cleanup would cancel the
  // chain and the next run would start over with `polls` back at 0. The budget
  // would never be spent and 'slow' would be unreachable — it would poll every
  // 3s for ever, which is precisely the state a lost webhook leaves you in, and
  // the one moment the "do not buy again" warning has to appear.
  // It gated on `!balance` only to mean "the first load has finished", which is
  // what `loading` says directly, without changing identity on every poll.
  useEffect(() => {
    if (topUp !== 'waiting') return undefined;
    // No workspace → nothing to poll against. Say so and offer the way out,
    // rather than leaving "we are crediting it" on screen with no poll behind
    // it. Same defect, and same remedy, as /billing/return on the subscription
    // side; it simply had not been carried across to the top-up return.
    if (!tenantId) { setTopUp('noWorkspace'); return undefined; }
    if (loading) return undefined;
    if (topUpBaseline.current === undefined) {
      topUpBaseline.current = parseDecimal(readTopUpBaseline(tenantId));
    }
    // No baseline (storage blocked, or this URL opened directly): we cannot
    // detect a RISE, and must not pretend to. The balance below is freshly
    // read, so say that and stop — never spin into "crediting is slow" on a
    // purchase whose outcome we simply cannot measure.
    if (topUpBaseline.current == null) { setTopUp('settled'); return undefined; }

    let cancelled = false;
    let polls = 0;
    let timer = null;

    const tick = () => {
      // `fresh`: bypass the 60 s browser cache, or every poll can be served
      // the same stored body and the rise stays invisible.
      fetchCreditBalance({ tenant: tenantId, fresh: true })
        .then((fresh) => {
          if (cancelled) return;
          const base = topUpBaseline.current;
          const now = parseDecimal(fresh?.remaining_credits);
          setBalance(fresh);
          if (base != null && now != null && now > base) {
            // ⭐ The page's own `balance` is not the only reader of this wallet:
            // the sidebar rail holds its own useEntitlements instance, and it is
            // mounted in AppShell so it survives this page. Without the nudge the
            // card would say 250 while the rail still said 200, until a run
            // refusal or a workspace switch happened to resync it — the exact
            // disagreement Phase 1 existed to remove.
            refreshEntitlements();
            setTopUp('credited');
            return;
          }
          if (++polls >= TOPUP_MAX_POLLS) { setTopUp('slow'); return; }
          timer = setTimeout(tick, TOPUP_POLL_MS);
        })
        // A failed read is not a verdict — keep waiting out the budget.
        .catch(() => {
          if (cancelled) return;
          if (++polls >= TOPUP_MAX_POLLS) { setTopUp('slow'); return; }
          timer = setTimeout(tick, TOPUP_POLL_MS);
        });
    };
    timer = setTimeout(tick, TOPUP_POLL_MS);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [topUp, tenantId, loading]);

  // Drop ?topup=done once this is settled, so a reload does not re-arm a poll
  // that can no longer see a rise. The notice survives it: it renders from
  // `topUp` state, which the URL only ever SEEDED.
  useEffect(() => {
    if (topUp && topUp !== 'waiting') {
      clearTopUpBaseline(tenantId);
      navigate('/credits', { replace: true });
    }
  }, [topUp, navigate, tenantId]);

  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  // Buckets carry only tenant_id. A customer-admin's `tenants[]` lists every
  // workspace their customer owns (one editor entry each), so it resolves names.
  const resolveTenantName = (id) => (tenants || []).find(x => x.tenant_id === id)?.display_name;

  return (
    // ⭐ 2026-09-27: the Gestione Workspace look (components/ui/SectionCard.jsx) — the
    // centred 1280px column and ONE section card titled with the page's <h1>. The
    // notices stay ABOVE the card (they are about the page, not the wallet), and the
    // "sign in again" prompt stays below it. Layout only: nothing here moved in logic.
    <div style={PAGE_COLUMN}>

      {error && (
        <div role="alert" style={{
          marginBottom: 16, padding: '8px 12px', borderRadius: 6,
          border: `1px solid ${COLOR.errFg}`, color: COLOR.errFg,
          background: SEMANTIC.errore.bg, fontSize: 12,
        }}>{error}</div>
      )}

      {topUp && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: RADII.controlInner, fontSize: 12,
            // Green only once there is something to be pleased about. 'waiting'
            // is work in progress and 'slow' is a warning — both amber. Painting
            // 'waiting' green read as "done" while the poll was still running.
            background: TOPUP_DONE.has(topUp) ? SEMANTIC.verde.bg : SEMANTIC.attenzione.bg,
            color: TOPUP_DONE.has(topUp) ? SEMANTIC.verde.fg : SEMANTIC.attenzione.fg,
            border: `1px solid ${TOPUP_DONE.has(topUp) ? SEMANTIC.verde.fg : SEMANTIC.attenzione.fg}33`,
          }}
        >
          <strong style={{ display: 'block', marginBottom: 2 }}>{t(`topup.return.${topUp}`)}</strong>
          {t(`topup.return.${topUp}Body`)}
          {topUp === 'noWorkspace' && (
            <div style={{ marginTop: 10 }}>
              <Link to="/select-tenant" style={{ color: SEMANTIC.attenzione.fg, fontWeight: 600 }}>
                {t('topup.return.pickWorkspace')}
              </Link>
            </div>
          )}
        </div>
      )}

      <SectionCard
        icon={Wallet}
        headingLevel={1}
        title={t('title')}
        subtitle={`${t('subtitle')}${activeTenant ? ` — ${activeTenant.display_name}` : ''}`}
      >
      <div style={{ ...CARD_BODY, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* No canAssignPlan here: this is the member/tenant-admin view and nobody on it
            can attach a subscription, so a blocked no-plan tenant gets the
            contact-your-administrator copy (plan assignment is superadmin-only). */}
        <CreditBalanceCard balance={balance} showMoney={isSuperadmin} loading={loading} />

        {/* ⭐ The route to the plans. It sits HERE, beside the balance, because
            that is where the question arises — not in the page header.
            ⚠️ Worded descriptively ("Piani disponibili"), never as a promise to
            upgrade: /billing is customer-admin only and a member who follows it
            meets the "riservata agli amministratori" panel. Naming the
            destination rather than the action keeps that honest. */}
        <div style={{ marginTop: -8 }}>
          <Link to="/billing" style={{ fontSize: 13, fontWeight: 600, color: COLORS.accent }}>
            {t('plansLink')}
          </Link>
        </div>

        <CreditUsageTable usage={usage} showMoney={isSuperadmin} loading={loading} />

        {/* ⭐ The customer's billing record MOVED to /billing-details (avatar menu,
            2026-09-24): it is the company's invoicing identity, not wallet content. */}

        {/* ⭐ Buying credit packs MOVED to /billing (Piani, 2026-09-24): the packs
            on sale and whether this customer may buy come from that screen's own
            read. The purchase still RETURNS here (?topup=done) — you bought
            credits, you watch them arrive on the wallet. */}

        {split && (
          <section aria-label={t('usage.titleByTenant')}>
            <CreditUsageTable
              usage={split}
              showMoney={isSuperadmin}
              groupBy="tenant"
              resolveTenantName={resolveTenantName}
              activeTenantId={tenantId}
            />
            <p style={{ color: COLOR.muted, fontSize: 12, margin: '8px 2px 0' }}>{t('split.note')}</p>
          </section>
        )}
      </div>
      </SectionCard>

      {/* The GRANTED half of the permissions drift (see auth/permissionsDrift.js).
          Customer-admin rights ride the login JWT, so someone promoted while
          signed in sees this page exactly as a plain member does and has no way
          to tell "I am not an admin" from "my token predates the promotion".
          There is no reliable automatic trigger for that — the two states are
          identical from here — so the remedy is offered as a quiet, discoverable
          line rather than an alert every member would see as noise. Clicking it
          raises the SAME notice, with the same message and the same swappable
          action, that a 403 raises in the revoked direction. */}
      <button
        type="button"
        onClick={notePermissionsGranted}
        style={{
          marginTop: 20, padding: 0, border: 'none', background: 'none',
          color: COLOR.muted, fontFamily: FONT, fontSize: 12,
          textDecoration: 'underline', cursor: 'pointer',
        }}
      >
        {tAuth('permissionsDrift.grantedPrompt')}
      </button>
    </div>
  );
}
